import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, setAuthToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Handles the Google OAuth callback.
 *
 * Flow:
 *   Google redirects → /auth/google/callback?code=xxx&state=xxx
 *   This page POSTs { code } to /api/auth/google/exchange
 *   Backend exchanges code with Google (using client_secret), returns JWT
 *   We store the JWT and redirect to dashboard (or `next` from state)
 */
export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const error = searchParams.get("error");

    // Google returned an error (e.g. user cancelled)
    if (error) {
      if (error === "access_denied") {
        navigate("/login", { replace: true });
      } else {
        setErrorMsg(`Google sign-in error: ${error}`);
      }
      return;
    }

    if (!code) {
      navigate("/login", { replace: true });
      return;
    }

    // Decode the `next` path from state
    let next = "/dashboard";
    if (state) {
      try {
        const decoded = JSON.parse(atob(state));
        if (decoded.next) next = decoded.next;
      } catch (_) {}
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/exchange", { code });
        if (data.token) setAuthToken(data.token);
        setUser(data.user);
        // Replace current history entry so back-button doesn't re-run the exchange
        navigate(next, { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail || "Google sign-in failed";
        setErrorMsg(detail);
      }
    })();
  }, [searchParams, navigate, setUser]);

  if (errorMsg) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--vp-ivory)]">
        <div className="text-center max-w-md px-6">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="font-heading text-xl mb-2">Sign-in failed</h2>
          <p className="text-sm text-[var(--vp-charcoal-soft)] mb-6">{errorMsg}</p>
          <a href="/login" className="text-[var(--vp-emerald)] underline underline-offset-4 text-sm font-medium">
            Back to login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-[var(--vp-ivory)]">
      <div className="text-center">
        <div className="mx-auto h-10 w-10 rounded-full border-2 border-[var(--vp-emerald)] border-t-transparent animate-spin" />
        <p className="mt-4 font-accent italic text-[var(--vp-charcoal-soft)]">Finishing sign-in…</p>
      </div>
    </div>
  );
}
